import { Search, AlertCircle, Users, Mail } from 'lucide-react'
import { Input } from '../../lib/shadcn/input'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import { StatusFilter } from './StatusFilter'
import { Chip } from './Chip'
import {
  type ApplicationRow,
  statusLabel,
  statusTone,
  formatDate,
  initials,
  emailTemplateLabel,
} from '../../utils/interviews'

export function PipelineList({
  applications,
  loading,
  error,
  search,
  onSearchChange,
  selectedStatuses,
  onStatusChange,
  selectedId,
  onSelect,
  showStatusFilter = true,
}: {
  applications: ApplicationRow[]
  loading: boolean
  error: string | null
  search: string
  onSearchChange: (v: string) => void
  selectedStatuses: string[]
  onStatusChange: (next: string[]) => void
  selectedId: string | null
  onSelect: (id: string) => void
  showStatusFilter?: boolean
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-2 border-b border-border p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by name, email or phone"
            className="pl-9"
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          {showStatusFilter ? (
            <StatusFilter selected={selectedStatuses} onChange={onStatusChange} />
          ) : (
            <span />
          )}
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
            {loading ? '…' : `${applications.length} result${applications.length === 1 ? '' : 's'}`}
          </span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error ? (
          <div className="m-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : loading && applications.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading applicants…
          </div>
        ) : applications.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <Users className="h-6 w-6" />
            No applicants match your filters.
          </div>
        ) : (
          <ul>
            {applications.map((app) => {
              const isSelected = app.application_id === selectedId
              return (
                <li key={app.application_id}>
                  <button
                    type="button"
                    onClick={() => onSelect(app.application_id)}
                    className={cn(
                      'relative flex w-full items-start gap-3 border-b border-border px-3 py-3 text-left transition-colors',
                      isSelected ? 'bg-[hsl(var(--brand-soft))]' : 'hover:bg-accent',
                    )}
                  >
                    {isSelected && (
                      <span className="absolute inset-y-0 left-0 w-1 rounded-r bg-[hsl(var(--brand))]" />
                    )}
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-xs font-semibold text-[hsl(var(--brand-soft-foreground))]">
                      {initials(app.full_name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-medium text-foreground">
                          {app.full_name || 'Unnamed applicant'}
                        </p>
                        <span className="shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
                          {app.score ?? '—'}
                        </span>
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {app.primary_email || app.primary_phone || '—'}
                      </p>
                      <div className="mt-1.5 flex items-center gap-2">
                        <Chip tone={statusTone(app.current_status)}>
                          {statusLabel(app.current_status)}
                        </Chip>
                        <span className="text-[11px] text-muted-foreground">
                          {formatDate(app.submitted_at)}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span className="truncate">
                          {app.last_email_sent_at
                            ? `${emailTemplateLabel(app.last_email_template_key)} · ${formatDate(app.last_email_sent_at)}`
                            : 'No email sent since migration'}
                        </span>
                      </div>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
